import React, { useEffect, useRef, useState } from "react";
import { Flex, Layout, Button, App as AntdApp, Typography, Tooltip } from "antd";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { RecorderProvider } from "./components/canvasRecorder/context/RecorderContext";
import CanvasRecorder from "./components/canvasRecorder/CanvasRecorder";
import { MetricsDashboard } from "./components/canvasRecorder/components/MetricsDashboard/MetricsDashboard";
import { ProjectsDashboard } from "./components/canvasRecorder/components/ProjectsDashboard";
import { ConfigProvider } from "antd";
import {
  getUser,
  isUserAuthenticated,
} from "./components/canvasRecorder/services/user";
import { useUserStore } from "./components/canvasRecorder/hooks/useUserStore";
import { FeatureFlagProvider } from "./components/canvasRecorder/services/featureFlags";
import { MigrationWrapper } from "./components/canvasRecorder/components/MigrationWrapper";
import { Crown, Home, LoaderCircle, Play } from "lucide-react";
import Logo from "./components/canvasRecorder/assets/images/logo.svg";
import "./App.css";
import AvatarComponent from "./components/canvasRecorder/components/AvatarComponent";
import { ProjectSettingsButton } from "./components/canvasRecorder/components/ProjectSettingsButton";
import { ExportButton } from "./components/canvasRecorder/components/DadanExport";
import SuspenseLoader from "./components/SuspenseLoader";
import {
  initGA,
  trackPageView,
  trackUpgradeFunnel,
} from "./components/canvasRecorder/services/analytics";
import { useAtom } from "jotai";
import { activeProjectAtom, sceneListAtom } from "./components/canvasRecorder/atoms/sceneAtoms";
import SceneService from "./components/canvasRecorder/services/sceneService";
import { projectsManager } from "./components/canvasRecorder/services/projectsManager";
import { ProjectPlayerModal } from "./components/canvasRecorder/components/modals/ProjectPlayerModal";
import { MediaStreamCleanup } from "./components/canvasRecorder/components/MediaStreamCleanup";
import { UpgradePrompt } from "./components/canvasRecorder/components/overlays/UpgradePrompt";
import { UpgradeModalProvider } from "./components/canvasRecorder/premiumFeatures";

const { Header, Content } = Layout;

function MainApp() {
  const { updateUserData, userData } = useUserStore();
  const userCalledRef = useRef<boolean>(false);
  const isAuthenticated = isUserAuthenticated();
  const [isLoading, setIsLoading] = useState(true);
  const [showApp, setShowApp] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check if in blank/quick recording mode
  const recordingMode = searchParams.get('mode') || 'project';
  const isBlankMode = recordingMode === 'blank';
  
  // Breadcrumb state for /recorder route
  const [activeProject, setActiveProject] = useAtom(activeProjectAtom);
  const [sceneList] = useAtom(sceneListAtom);
  const [isSavingProjectTitle, setIsSavingProjectTitle] = useState(false);
  const [isProjectPlayerVisible, setIsProjectPlayerVisible] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    // Initialize Google Analytics
    initGA();
    trackPageView("/");

    const loadUserRoot = async () => {
      userCalledRef.current = true;
      try {
        const response = await getUser(undefined, true);
        updateUserData(response);
      } catch (error) {
        console.error("Failed to load user root data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    if (!userCalledRef.current) {
      loadUserRoot();
    } else {
      setIsLoading(false);
    }
  }, []);

  const handleProjectTitleChange = async (newTitle: string) => {
    if (!activeProject || !newTitle.trim()) return;

    setIsSavingProjectTitle(true);
    try {
      console.log('💾 [App] Updating project title:', { projectId: activeProject.id, newTitle });
      
      // Update via projectsManager for cloud projects (numeric ID)
      if (!isNaN(Number(activeProject.id))) {
        await projectsManager.updateProject(Number(activeProject.id), { name: newTitle.trim() });
      } else {
        // Update local project via SceneService
        await SceneService.updateProject(activeProject.id, { title: newTitle.trim() });
      }
      
      // Update local state to reflect the change immediately
      setActiveProject({
        ...activeProject,
        title: newTitle.trim()
      });
      
      console.log('✅ [App] Project title updated successfully');
    } catch (error) {
      console.error('❌ [App] Failed to update project title:', error);
    } finally {
      setIsSavingProjectTitle(false);
    }
  };

  const handleSignIn = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Track sign in attempt
    trackPageView("/sign-in");

    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    if (baseUrl) {
      window.location.assign(`${baseUrl}/signIn?source=recorder`);
    } else {
      console.warn("REACT_APP_BACKEND_URL is not set");
    }
  };

  const handleSignUp = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Track sign up attempt
    trackPageView("/sign-up");

    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    if (baseUrl) {
      window.location.assign(`${baseUrl}/signUp?source=recorder`);
    } else {
      console.warn("REACT_APP_BACKEND_URL is not set");
    }
  };

  const handleUpgrade = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    trackUpgradeFunnel.buttonClick('navbar');
    setShowUpgradeModal(true);
  };

  const headerStyle: React.CSSProperties = {
    color: "#000",
    height: 64,
    paddingInline: 32,
    lineHeight: "64px",
    backgroundColor: "#fff",
    borderBottom: "1px solid #e5e7eb",
  };

  const contentStyle: React.CSSProperties = {
    textAlign: "center",
    height: "calc(100vh - 64px)",
    lineHeight: "normal",
    color: "#000",
    backgroundColor: "#f5f5f5",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    overflow: "hidden",
  };

  const handleLoadingComplete = () => {
    setShowApp(true);
  };

  return (
    <>
      <SuspenseLoader
        isLoading={isLoading}
        onLoadingComplete={handleLoadingComplete}
      />
      {showApp && (
        <ConfigProvider
          theme={{
            token: {
              colorPrimary: "#4A3CFA",
              colorPrimaryActive: "#4A3CFA",
              colorPrimaryBgHover: "#4A3CFA",
              colorPrimaryBorder: "#4A3CFA",
              colorPrimaryHover: "#4A3CFA",
              colorLink: "#231932",
              borderRadius: 6,
              fontWeightStrong: 500,
              colorSplit: "#616161",
              colorBgContainerDisabled: "rgba(127, 17, 224, .16)",
              colorTextDisabled: "rgba(0,0,0,0.48)",
              fontFamily:
                "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
            },
            components: {
              Menu: {
                activeBarHeight: 0,
                activeBarBorderWidth: 0,
              },
              Button: {
                contentFontSizeLG: 14,
                borderColorDisabled: "rgba(127, 17, 224, 0)",
                colorPrimaryBg: "rgba(74,60,250, 0.12)",
                colorPrimaryBgHover: "rgba(74,60,250, 0.16)",
              },
              Form: {
                itemMarginBottom: 0,
              },
            },
          }}
        >
          <FeatureFlagProvider userId={userData?.id || userData?.userId}>
            <MigrationWrapper>
              <RecorderProvider>
                <UpgradeModalProvider>
                <MediaStreamCleanup />
                <div
                className="App"
                style={{ height: "100vh", overflow: "hidden" }}
              >
              <Layout style={{ height: "100vh", overflow: "hidden" }}>
                <Header style={headerStyle}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: "100%",
                    }}
                  >
                    <img
                      src={Logo}
                      alt="Logo"
                      style={{
                        height: "32px",
                        width: "auto",
                        marginRight: "32px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        const url = process.env.REACT_APP_BACKEND_URL;
                        if (url) {
                          window.location.assign(url);
                        }
                      }}
                    />

                    {/* Breadcrumb navigation - consistent across all pages */}
                    <Flex 
                      align="center" 
                      gap={8} 
                      style={{ 
                        flex: 1, 
                        minWidth: 0,
                        fontSize: '14px',
                        color: '#6B7280'
                      }}
                    >
                      {/* First level: Video Studio - always visible, links to projects dashboard */}
                      <Flex align="center" gap={6} style={{ flexShrink: 0 }}>
                        <span 
                          style={{ 
                            cursor: location.pathname === '/recorder' ? 'pointer' : 'default',
                            color: location.pathname === '/recorder' ? '#4A3CFA' : '#6B7280',
                            fontWeight: 500,
                            flexShrink: 0
                          }}
                          onClick={() => {
                            if (location.pathname === '/recorder') {
                              navigate('/');
                            }
                          }}
                        >
                          Video Studio
                        </span>
                      </Flex>

                      {/* Second level: Project title - only on /recorder route with active project */}
                      {location.pathname === '/recorder' && activeProject && (
                        <>
                          <span style={{ color: '#D1D5DB', flexShrink: 0 }}>/</span>
                          <Typography.Text
                            editable={
                              isSavingProjectTitle
                                ? false
                                : {
                                    onChange: handleProjectTitleChange,
                                    tooltip: 'Click to edit project title',
                                    maxLength: 120,
                                    autoSize: { maxRows: 1 },
                                    enterIcon: null,
                                  }
                            }
                            style={{
                              fontSize: '14px',
                              fontWeight: 600,
                              color: '#111827',
                              cursor: isSavingProjectTitle ? 'default' : 'text',
                              opacity: isSavingProjectTitle ? 0.6 : 1,
                              transition: 'opacity 0.2s ease',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '300px'
                            }}
                          >
                            {activeProject.title || 'Untitled Project'}
                          </Typography.Text>
                          {isSavingProjectTitle && (
                            <LoaderCircle 
                              size={14} 
                              style={{ 
                                flexShrink: 0,
                                animation: 'spin 1s linear infinite'
                              }} 
                            />
                          )}
                        </>
                      )}

                      {/* Auth buttons - always on the right */}
                      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {/* Master Preview Button - Show for scene-based projects with recorded scenes */}
                        {location.pathname === '/recorder' && activeProject && !isBlankMode && sceneList.some(scene => scene.status === 'recorded') && (
                          <Tooltip title={<span style={{ textAlign: 'center', display: 'block' }}>Play Entire Video</span>} placement="bottom">
                            <Button
                              type="text"
                              size="large"
                              icon={<Play size={18} fill="#4A3CFA" color="#4A3CFA" />}
                              onClick={() => setIsProjectPlayerVisible(true)}
                              style={{
                                width: 40,
                                height: 40,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                borderRadius: 8,
                              }}
                              aria-label="Preview all scenes"
                            />
                          </Tooltip>
                        )}
                        {/* Project Settings Button - Show for scene-based projects only (not blank/quick mode) */}
                        {location.pathname === '/recorder' && activeProject && !isBlankMode && (
                          <ProjectSettingsButton />
                        )}
                        {/* Export Button - Show for scene-based projects with multiple recorded scenes */}
                        {location.pathname === '/recorder' && activeProject && !isBlankMode && (
                          <ExportButton />
                        )}
                        {!isAuthenticated ? (
                          <>
                            <Button
                              type="primary"
                              ghost
                              size="large"
                              onClick={handleSignIn}
                            >
                              Sign in
                            </Button>
                            <Button
                              type="primary"
                              size="large"
                              onClick={handleSignUp}
                            >
                              Get dadan for free
                            </Button>
                          </>
                        ) : (
                          <>
                            {userData && !userData.isPaid && (
                              <Button
                                color="primary"
                                variant="filled"
                                size="large"
                                onClick={handleUpgrade}
                                style={{ color: "#12022f" }}
                                icon={
                                  <Crown
                                    size={16}
                                    fill="#ffb229"
                                    color="#ffb229"
                                  />
                                }
                              >
                                Upgrade Now!
                              </Button>
                            )}
                            <AvatarComponent />
                          </>
                        )}
                      </div>
                    </Flex>
                  </div>
                </Header>
                <Content style={contentStyle}>
                  <Routes>
                    <Route path="/" element={<ProjectsDashboard />} />
                    <Route path="/recorder" element={<CanvasRecorder />} />
                    <Route path="/metrics-dashboard" element={<MetricsDashboard />} />
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Content>

                {/* Project Player Modal - Master play button for entire project */}
                <ProjectPlayerModal
                  visible={isProjectPlayerVisible}
                  projectId={activeProject?.id}
                  scenes={sceneList}
                  onClose={() => setIsProjectPlayerVisible(false)}
                />

                {/* Upgrade Prompt Modal - navbar specific */}
                <UpgradePrompt
                  visible={showUpgradeModal}
                  onClose={() => setShowUpgradeModal(false)}
                  source="navbar"
                />
              </Layout>
            </div>
                </UpgradeModalProvider>
              </RecorderProvider>
            </MigrationWrapper>
          </FeatureFlagProvider>
        </ConfigProvider>
      )}
    </>
  );
}

function App() {
  return (
    <Router>
      <AntdApp>
        <MainApp />
      </AntdApp>
    </Router>
  );
}

export default App;
